import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import moment, { Moment } from "moment-timezone";
import MobileWheelTimePicker from "./MobileWheelTimePicker";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  setErrors,
  setScheduleTime,
  updateIndividualSigner,
} from "redux/appSlice";
import {
  BACKEND_TIMEZONE,
  convertEstToLocalTimezone,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { RequestParticipant } from "models/interfaces";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";

interface MobileDelayDateTimeSheetProps {
  open: boolean;
  onClose: () => void;
  signerToDelay?: Partial<RequestParticipant> | Partial<RequestParticipant>[];
  /** Sheet title (defaults to the delay-signer wording). */
  title?: string;
  /**
   * When true the sheet schedules the whole envelope (setScheduleTime) instead
   * of delaying an individual signer, but keeps the identical UI.
   */
  isScheduleReq?: boolean;
}

/**
 * Bottom-sheet date/time picker for "Delay by date and time".
 * Reuses the same delay persistence as the desktop DateAndTimeSelector
 * (updateIndividualSigner with a scheduleTime in EST). When `isScheduleReq`
 * is set it instead schedules the envelope (setScheduleTime) with the same UI.
 */
const MobileDelayDateTimeSheet: React.FC<MobileDelayDateTimeSheetProps> = ({
  open,
  onClose,
  signerToDelay = {},
  title = "Delay by date and time",
  isScheduleReq = false,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const signRequest = useAppSelector((s: RootState) => s.app.signRequest);

  const [tab, setTab] = useState<"date" | "time">("date");
  const [value, setValue] = useState<Moment | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab("date");
    // Pre-fill with the existing value (stored in EST). For schedule mode this
    // is the envelope scheduleTime; otherwise the signer's existing delay.
    const existing = isScheduleReq
      ? signRequest.scheduleTime
      : (Array.isArray(signerToDelay) ? signerToDelay[0] : signerToDelay)
          ?.scheduleTime;
    if (existing) {
      setValue(convertEstToLocalTimezone(existing, "MM/DD/YYYY HH:mm:ss"));
    } else {
      setValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDatePick = (d: Moment | null) => {
    if (!d) return;
    const next = (value ? value.clone() : moment())
      .year(d.year())
      .month(d.month())
      .date(d.date());
    setValue(next);
  };

  const handleTimePick = (t: Moment | null) => {
    if (!t) return;
    const next = (value ? value.clone() : moment())
      .hour(t.hour())
      .minute(t.minute())
      .second(0);
    setValue(next);
  };

  const applyDelay = () => {
    if (!value) return;
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Sent back in the backend's zone (UTC) — see BACKEND_TIMEZONE.
    const est = moment.tz(value, userTz).tz(BACKEND_TIMEZONE);

    // Schedule mode: set the envelope schedule time and exit.
    if (isScheduleReq) {
      dispatch(setScheduleTime(est.format("MM/DD/YYYY HH:mm:ss")));
      onClose();
      return;
    }

    const envelopeDate = signRequest.scheduleTime;
    if (envelopeDate) {
      const envelopeLocal = convertEstToLocalTimezone(
        envelopeDate,
        "MM/DD/YYYY HH:mm:ss",
      );
      if (!envelopeLocal.isBefore(value)) {
        openErrorNotification(
          "Delayed signer date/time must be after scheduled envelope date/time.",
        );
        return;
      }
    }

    const fields = {
      delayDays: undefined,
      delayHours: undefined,
      scheduleTime: est.format("MM/DD/YYYY HH:mm:ss"),
    };
    const targets = Array.isArray(signerToDelay)
      ? signerToDelay
      : [signerToDelay];
    targets.forEach((s) => {
      if (isTruthy(s?.id) || s?.id === 0) {
        dispatch(
          updateIndividualSigner({ signerId: s.id, fieldsToUpdate: fields }),
        );
      }
    });
    dispatch(setErrors({ delay: "" }));
    onClose();
  };

  const selectedLabel = !value
    ? "—"
    : tab === "time"
      ? value.format("MMM D, YYYY h:mm A")
      : value.format("MMM D, YYYY");

  const calendarSx = {
    width: "100%",
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "1rem",
      fontWeight: 600,
      color: m.base[500],
    },
    "& .MuiDayCalendar-weekDayLabel": {
      color: m.base[900],
      fontWeight: 700,
    },
    "& .MuiPickersDay-root": {
      color: m.base[900],
      fontWeight: 500,
      "&.Mui-selected": {
        backgroundColor: `${m.ePurple[50]} !important`,
        color: m.ePurple[700],
        fontWeight: 700,
      },
      "&.MuiPickersDay-dayOutsideMonth": { color: m.base[100] },
      "&:focus.Mui-selected": {
        backgroundColor: `${m.ePurple[50]} !important`,
      },
    },
    "& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton":
      { color: m.base[500] },
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          px: 2,
          pb: 3,
        },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "delay-datetime-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          id="delay-datetime-sheet-title"
          component="h2"
          sx={{ fontSize: "1rem", fontWeight: 600, color: m.base[900] }}
        >
          {title}
        </Typography>
        <Typography
          {...pressableProps(() => setValue(null), { label: "Clear" })}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: m.grey[400],
            cursor: "pointer",
          }}
        >
          Clear
        </Typography>
      </Box>

      {/* Date / Time tabs */}
      <Box sx={classes.tabsRow} role="tablist" aria-label="Choose date or time">
        <Box
          sx={classes.tab(tab === "date")}
          {...pressableProps(() => setTab("date"), {
            role: "tab",
            selected: tab === "date",
            selectedAttr: "aria-selected",
          })}
        >
          Date
        </Box>
        <Box
          sx={classes.tab(tab === "time")}
          {...pressableProps(() => setTab("time"), {
            role: "tab",
            selected: tab === "time",
            selectedAttr: "aria-selected",
          })}
        >
          Time
        </Box>
      </Box>

      {/* Content */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        {tab === "date" ? (
          <DateCalendar
            value={value}
            onChange={handleDatePick}
            disablePast
            views={["day"]}
            showDaysOutsideCurrentMonth
            sx={calendarSx}
          />
        ) : (
          <Box sx={{ py: 1 }}>
            <MobileWheelTimePicker value={value} onChange={handleTimePick} />
          </Box>
        )}
      </LocalizationProvider>

      {/* Selected summary */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: m.ePurple[50],
          borderRadius: "14px",
          px: 2,
          py: 1.75,
          mt: 1,
        }}
      >
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: m.base[500] }}
        >
          Selected
        </Typography>
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 700, color: m.ePurple[700] }}
        >
          {selectedLabel}
        </Typography>
      </Box>

      {/* Apply */}
      <Button
        fullWidth
        variant="contained"
        disableElevation
        disabled={!value}
        onClick={applyDelay}
        sx={{ ...classes.continueButton, width: "100%", mt: 2, py: 1.75 }}
      >
        Apply
      </Button>
    </Drawer>
  );
};

export default MobileDelayDateTimeSheet;
